import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Tent, MapPin, CalendarDays, Trash2, Pencil, Lock, ArrowRight, Receipt } from 'lucide-react';
import { SearchableSelect } from '../../components/ui/searchable-select';
import {
  listEvents, createEvent, updateEvent, updateEventStatus, deleteEvent,
} from './api';
import { getCustomers } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const STATUS_FLOW = ['planning', 'active', 'reconciling', 'closed'];

const STATUS_META = {
  planning: { label: 'Planning', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  reconciling: { label: 'Reconciling', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  closed: { label: 'Closed', className: 'bg-zinc-200 text-zinc-700 border-zinc-300' },
};

const emptyForm = { name: '', location: '', start_date: '', end_date: '', notes: '', invoice_customer_id: '' };

export default function Events() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEvents();
      setEvents(res.data || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getCustomers().then((res) => setCustomers(res.data || [])).catch(() => {});
  }, []);

  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      name: ev.name || '',
      location: ev.location || '',
      start_date: ev.start_date || '',
      end_date: ev.end_date || '',
      notes: ev.notes || '',
      invoice_customer_id: ev.invoice_customer_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Event name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes || null,
        invoice_customer_id: form.invoice_customer_id || (editing ? '' : null),
      };
      if (editing) {
        await updateEvent(editing.id, payload);
        toast.success('Event updated');
      } else {
        await createEvent(payload);
        toast.success('Event created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (ev, status) => {
    try {
      await updateEventStatus(ev.id, status);
      toast.success(`Status set to ${STATUS_META[status].label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEvent(deleteTarget.id);
      toast.success('Event deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete event');
    }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Tent className="w-7 h-7 text-emerald-600" /> Events
          </h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
            Manage trade shows and pop-up events as temporary destinations. Stock sent to an
            event stays your product until it is consumed (sold / sampled / wasted) or returned.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-2" /> New Event
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : events.length === 0 ? (
        <Card data-testid="events-empty">
          <CardContent className="py-16 text-center">
            <Tent className="w-12 h-12 mx-auto text-muted-foreground/40" />
            <h3 className="text-lg font-medium mt-4">No events yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first event to start tracking trade-show stock.
            </p>
            <Button onClick={openCreate} className="mt-4" variant="outline" data-testid="empty-new-event-btn">
              <Plus className="w-4 h-4 mr-2" /> New Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {events.map((ev) => {
            const meta = STATUS_META[ev.status] || STATUS_META.planning;
            const isClosed = ev.status === 'closed';
            return (
              <Card key={ev.id} data-testid={`event-card-${ev.id}`} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg leading-tight flex items-center gap-2">
                      {ev.name}
                      {isClosed && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                    </CardTitle>
                    <Badge variant="outline" className={meta.className} data-testid={`event-status-${ev.id}`}>
                      {meta.label}
                    </Badge>
                  </div>
                  <CardDescription className="space-y-1 pt-1">
                    {ev.location && (
                      <span className="flex items-center gap-1.5 text-xs">
                        <MapPin className="w-3.5 h-3.5" /> {ev.location}
                      </span>
                    )}
                    {(ev.start_date || ev.end_date) && (
                      <span className="flex items-center gap-1.5 text-xs">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {ev.start_date || '—'} {ev.end_date ? `→ ${ev.end_date}` : ''}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-xs">
                      <Receipt className="w-3.5 h-3.5" />
                      {ev.invoice_customer_name
                        ? `Invoices to: ${ev.invoice_customer_name}`
                        : <span className="text-muted-foreground italic">Invoice customer not set</span>}
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-between gap-4">
                  {ev.notes && (
                    <p className="text-sm text-muted-foreground line-clamp-3">{ev.notes}</p>
                  )}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Status</span>
                      <Select
                        value={ev.status}
                        onValueChange={(v) => handleStatusChange(ev, v)}
                        disabled={isClosed && !isAdmin}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`event-status-select-${ev.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_FLOW.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`event-status-option-${s}`}>
                              {STATUS_META[s].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(`/events/${ev.id}`)}
                        data-testid={`open-event-${ev.id}`}
                      >
                        Open <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(ev)}
                        disabled={isClosed}
                        data-testid={`edit-event-${ev.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(ev)}
                          data-testid={`delete-event-${ev.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Event' : 'New Event'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update this event\'s details.' : 'Create a temporary destination for a trade show or pop-up.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="event-name">Event Name *</Label>
              <Input
                id="event-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. BLOOM 2026"
                maxLength={120}
                data-testid="event-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-location">Location</Label>
              <Input
                id="event-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Phoenix Park, Dublin"
                maxLength={200}
                data-testid="event-location-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="event-start">Start Date</Label>
                <Input
                  id="event-start"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="event-start-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="event-end">End Date</Label>
                <Input
                  id="event-end"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="event-end-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="event-notes">Notes</Label>
              <Textarea
                id="event-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes about this event"
                rows={3}
                data-testid="event-notes-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Invoice Customer (AccountsIQ)</Label>
              <SearchableSelect
                options={customerOptions}
                value={form.invoice_customer_id}
                onValueChange={(v) => setForm({ ...form, invoice_customer_id: v })}
                placeholder="Select the account the totalled event invoice posts to…"
                searchPlaceholder="Search customers…"
                data-testid="event-invoice-customer-select"
              />
              <p className="text-xs text-muted-foreground">
                An existing customer account (e.g. SHOP / TRADE SHOW) used specifically for the
                event's totalled sales invoice. Required before pushing to AccountsIQ.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Save Changes' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-event-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deleteTarget?.name}". This action is logged and can be reversed
              by an administrator if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-event-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
